import { MINING_FLEET_LIMIT } from './balance';

/**
 * Where the docked ships sit, and which way they face.
 *
 * ## Rings, not a ring
 *
 * The first version had ONE ring and a twelve-ship ceiling, and the ceiling was
 * the ring: a thirteenth ship had nowhere to stand, so the fleet stopped at
 * twelve and the interface said "12/12" as though that were an economic rule.
 * It never was. A ring fills, and the next ship starts a wider one - so the
 * limit on the fleet is the limit on the fleet, and the drawing follows it
 * instead of deciding it.
 *
 * ## Constant spacing, by construction
 *
 * Each ring is `RING_RADIUS_STEP` further out and holds proportionally more
 * ships, so the chord between neighbours barely moves from the innermost ring
 * to the outermost - 0.5176 to 0.5374 in units of the base radius. Spacing that
 * degraded with distance would mean the outer rings looked crowded exactly when
 * the fleet was most impressive.
 *
 * ## Pure, so it can be checked rather than looked at
 *
 * "Do the ships overlap", "does anything sit on the planet" and "do they all
 * face the centre" are questions about geometry, and geometry can be tested.
 * Putting the maths in the scene would make them questions about a screenshot,
 * which is the kind of check that passes right up until somebody docks a
 * hundredth ship.
 */

/** How many ships the innermost ring holds. */
export const RING_BASE_SLOTS = 12;

/**
 * How much further out each ring sits, as a fraction of the base radius.
 *
 * 0.35 is what makes the capacities come out at 12, 16, 20, 24, 28 - which sum
 * to exactly 100 across five rings. The safety limit and the geometry agree by
 * arithmetic rather than by coincidence, and neither had to be rounded to make
 * the other work.
 */
export const RING_RADIUS_STEP = 0.35;

/** Radius of ring `index`, in units of the base ring's radius. */
export function ringRadiusFactor(index: number): number {
  return 1 + index * RING_RADIUS_STEP;
}

/**
 * How many ships ring `index` holds.
 *
 * Proportional to its circumference, which is what keeps the gap between
 * neighbours constant as the rings grow.
 */
export function ringCapacity(index: number): number {
  return Math.floor(RING_BASE_SLOTS * ringRadiusFactor(index));
}

/**
 * The capacities, innermost first, up to the safety limit.
 *
 * `[12, 16, 20, 24, 28]` - cumulative `[12, 28, 48, 72, 100]`.
 */
export const RING_CAPACITIES: readonly number[] = Object.freeze(
  (() => {
    const capacities: number[] = [];
    let placed = 0;
    for (let index = 0; placed < MINING_FLEET_LIMIT; index += 1) {
      const capacity = ringCapacity(index);
      capacities.push(capacity);
      placed += capacity;
    }
    return capacities;
  })(),
);

/** How many rings a fleet of `count` ships occupies. */
export function ringsForCount(count: number): number {
  if (!Number.isSafeInteger(count) || count <= 0) {
    return 0;
  }
  let remaining = Math.min(count, MINING_FLEET_LIMIT);
  let rings = 0;
  while (remaining > 0) {
    remaining -= ringCapacity(rings);
    rings += 1;
  }
  return rings;
}

/** Radians. Zero points right; each ring is walked anticlockwise from the top. */
export interface RingSlot {
  /** Which ring, 0 for the innermost. */
  readonly ring: number;
  /** Where the ship sits, in radians from the positive X axis. */
  readonly angle: number;
  /** This ring's radius, in units of the base ring's radius. Always >= 1. */
  readonly radius: number;
  /** Offset from the planet, already scaled by `radius`. */
  readonly x: number;
  readonly y: number;
  /**
   * The rotation that makes a ship point AT the planet.
   *
   * Derived from the position rather than stored beside it, so the two can
   * never disagree: a ship that moved and did not turn would be facing empty
   * space, which is exactly the defect this exists to make impossible.
   */
  readonly facing: number;
}

/** Straight up. One ship goes here, and every ring is built around it. */
const TOP = -Math.PI / 2;

/**
 * The slots for `count` ships, filling each ring before starting the next.
 *
 * Every ring is uniform in itself: two ships on a ring are a half turn apart,
 * four are a quarter, twelve are a twelfth. Alternate rings are rotated by half
 * a step so the fleet does not form radial spokes - purely how it looks, and it
 * cannot move a ship onto another because the offset is within its own ring.
 */
export function ringLayout(count: number): readonly RingSlot[] {
  if (!Number.isSafeInteger(count) || count <= 0) {
    return [];
  }
  const ships = Math.min(count, MINING_FLEET_LIMIT);
  const slots: RingSlot[] = [];
  let placed = 0;
  for (let ring = 0; placed < ships; ring += 1) {
    const capacity = ringCapacity(ring);
    /*
     * A partly-filled ring spreads its ships over the WHOLE ring rather than
     * bunching them into the first arc: thirteen ships are twelve plus one
     * alone on a wider ring, and that one belongs at the top, not wherever the
     * thirteenth twelfth of a circle happens to be.
     */
    const onThisRing = Math.min(capacity, ships - placed);
    const radius = ringRadiusFactor(ring);
    const step = (Math.PI * 2) / onThisRing;
    // Half a step of stagger on odd rings, so nothing lines up radially.
    const offset = ring % 2 === 0 ? 0 : Math.PI / capacity;
    for (let index = 0; index < onThisRing; index += 1) {
      const angle = TOP + offset + index * step;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      slots.push({
        ring,
        angle,
        radius,
        x,
        y,
        /*
         * Pointing INWARD: the vector from the ship to the planet is the
         * negative of its position, so the facing is the position's angle
         * turned half a turn. Computed here so no caller has to remember the
         * sign.
         */
        facing: Math.atan2(-y, -x),
      });
    }
    placed += onThisRing;
  }
  return slots;
}

/**
 * The smallest distance between any two ships, in units of the base radius.
 *
 * What "no ship overlaps another" reduces to. Measured across the whole layout
 * rather than within one ring, because two ships on ADJACENT rings can be
 * closer to each other than either is to its own neighbours.
 */
export function smallestSlotDistance(count: number): number {
  const slots = ringLayout(count);
  if (slots.length < 2) {
    return Number.POSITIVE_INFINITY;
  }
  let smallest = Number.POSITIVE_INFINITY;
  for (let left = 0; left < slots.length; left += 1) {
    for (let right = left + 1; right < slots.length; right += 1) {
      const a = slots[left];
      const b = slots[right];
      if (!a || !b) {
        continue;
      }
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (distance < smallest) {
        smallest = distance;
      }
    }
  }
  return smallest;
}

/**
 * Whether `count` ships fit around a base ring of `radius` without touching.
 *
 * The straight-line distance between the two closest ships against the width a
 * ship is drawn at. Two ships are boxes, and it is the distance between their
 * centres that decides whether they collide.
 */
export function ringFits(count: number, radius: number, shipWidth: number): boolean {
  if (count < 2) {
    return true;
  }
  return smallestSlotDistance(count) * radius >= shipWidth;
}

/**
 * The base radius that puts the OUTERMOST ring at `availableRadius`.
 *
 * The fleet grows outward, and the drawing has a fixed amount of room, so the
 * whole layout shrinks to fit rather than spilling off the stage. One ship uses
 * the full radius; a hundred pack five rings into the same circle.
 */
export function baseRadiusFor(count: number, availableRadius: number): number {
  const rings = ringsForCount(count);
  if (rings <= 0) {
    return availableRadius;
  }
  return availableRadius / ringRadiusFactor(rings - 1);
}

/**
 * How wide a ship may be drawn without touching its neighbour, in pixels.
 *
 * The closest pair, times the base radius, with a margin. Returned rather than
 * assumed so the scene never has to guess: at a hundred ships the hulls are
 * genuinely smaller, and that is the honest way to draw a hundred ships.
 */
export function shipWidthFor(count: number, baseRadius: number, maximum: number): number {
  if (count < 2) {
    return maximum;
  }
  return Math.min(maximum, smallestSlotDistance(count) * baseRadius * 0.85);
}
